/**
 * 问卷配置类型。
 * 注：原 types.ts 未包含在 source map 里（纯类型文件不产码），本文件按还原代码的用法重建；
 * 字段与后端 validate.mjs 的校验规则对齐，并留有索引签名以兼容未知扩展键。
 */

export type QuestionType = 'text' | 'longtext' | 'single' | 'multi' | 'slider' | 'select' | 'matrix' | 'matrixtext' | 'matrixslider' | 'ranking' | 'file' | 'statement' | 'pagebreak';

/** 不参与作答、可不参与必填校验与评分的题型（标题说明 / 分页栏） */
export const NON_ANSWER_TYPES: readonly QuestionType[] = ['statement', 'pagebreak'];

export function isNonAnswerType(type: QuestionType | undefined): boolean {
  return !!type && NON_ANSWER_TYPES.includes(type);
}

/** 带 options 的选择题型（0902：可给选项开「附属填空」） */
export const CHOICE_TYPES: readonly QuestionType[] = ['single', 'multi', 'select'];

export function isChoiceType(type: QuestionType | string | undefined): boolean {
  return !!type && (CHOICE_TYPES as readonly string[]).includes(type);
}

/** 有选数限制（minSelect / maxSelect）的题型：多选题、排序题（0903），与后端 answer-rules.mjs 同名 */
export const SELECT_LIMIT_TYPES: readonly QuestionType[] = ['multi', 'ranking'];

export type ConditionOp = 'equals' | 'contains' | 'gt' | 'lt' | 'between';

export type Condition = { question: string; op: ConditionOp; value: any };

export type DisplayLogic = { operator: 'AND' | 'OR'; conditions: Condition[] };

/* ---------------- 1627 题目关联（轻量级显示逻辑） ----------------
 * 题目关联是「显示逻辑」的简化版：每条规则只描述「前置题目选中指定选项」一个条件，
 * 多条之间是 OR 关系（任一命中即显示）。复用了 displayLogic 的 AND/OR 引擎来保证
 * 「已删题则视为不可见」等规则一致。 */

/** 一条关联规则：当前题仅在前置题（questionId）选中指定选项时显示。
 *  - 选择题（single/multi/select/ranking）：填 questionId + optionValue
 *  - 矩阵题（matrix）：再填 row，触发 = answers[questionId][row] === optionValue
 *  - 矩阵填空题（matrixtext）下拉行：填 questionId + row + optionValue
 *  - 矩阵填空题（matrixtext）文本行：填 questionId + row + op(+matchText)；
 *    op ∈ equals|contains|isNotEmpty|isEmpty；equals/contains 需 matchText
 */
export type Correlation = {
  questionId: string;
  optionValue?: string;
  row?: string;
  op?: 'equals' | 'contains' | 'isNotEmpty' | 'isEmpty';
  matchText?: string;
};

/** 题目上的关联配置：数组表示多条 OR 规则；空数组/undefined 等价于"无关联" */
export function evaluateCorrelation(corr: Correlation[] | undefined, answers: Record<string, any>): boolean {
  if (!corr || corr.length === 0) return true;
  return corr.some((c) => {
    if (!c || typeof c.questionId !== 'string') return false;
    const ans = answers?.[c.questionId];
    if (ans === undefined || ans === null) return false;
    // matrixtext 文本行：op 模式
    if (typeof c.op === 'string' && c.op !== '') {
      if (typeof c.row !== 'string' || c.row === '') return false;
      if (!ans || typeof ans !== 'object' || Array.isArray(ans)) return false;
      const raw = (ans as Record<string, unknown>)[c.row];
      const cellStr = raw === undefined || raw === null ? '' : String(raw);
      if (c.op === 'equals') return typeof c.matchText === 'string' && cellStr === c.matchText;
      if (c.op === 'contains') return typeof c.matchText === 'string' && c.matchText !== '' && cellStr.includes(c.matchText);
      if (c.op === 'isNotEmpty') return cellStr.trim() !== '';
      if (c.op === 'isEmpty') return cellStr.trim() === '';
      return false;
    }
    // 旧：选项精确匹配（选择题 / matrix 量表 / matrixtext 下拉行）
    if (typeof c.optionValue !== 'string' || c.optionValue === '') return false;
    // 矩阵触发：答案形态 { 行: 值 }，取 ans[row] === optionValue
    if (typeof c.row === 'string' && c.row !== '') {
      if (!ans || typeof ans !== 'object' || Array.isArray(ans)) return false;
      return String((ans as Record<string, unknown>)[c.row] ?? '') === c.optionValue;
    }
    if (Array.isArray(ans)) return ans.some((v) => String(v) === c.optionValue);
    return String(ans) === c.optionValue;
  });
}

/* ---------------- 0907 选项关联（选择题选项级联动） ----------------
 * 配置在「目标题 B」上：每条 { questionId: 源题 A, optionValue: A 的某个选项, options: B 里要展示的选项值 }。
 * 填写端：A 选中某选项时，B 只展示所有命中规则 options 的并集；没有任何规则命中 → B 整题不显示不渲染。
 * 提交 / 导出只保留 B 里仍然可见的选项（前端 pruneOptionLinkAnswers + 服务端 sanitizeOptionLinkAnswers 兜底）。 */

export type OptionLink = { questionId: string; optionValue: string; options: string[] };

/* ---------------- 0904 跳题逻辑 ---------------- */

/** 跳转目标：next=顺序下一题；end=末尾结束作答；submitInvalid=直接提交无效卷；question=跳到指定题 */
export type JumpTarget =
  | { kind: 'next' }
  | { kind: 'end' }
  | { kind: 'submitInvalid' }
  | { kind: 'question'; qid: string };

/**
 * 跳题逻辑：
 *  - byOption：仅选择题型（single/multi/select）有；key = option.value。多选题只对"任一被勾选"的目标触发（按勾选顺序取第一个匹配的）
 *  - unconditional：任何题型可配；填写者答完此题后无条件跳到目标（覆盖 byOption，若同时配了）
 *  - 二者都缺省 → 答完此题顺序进入下一题（默认 next）
 */
export type JumpLogic = {
  byOption?: Record<string, JumpTarget>;
  unconditional?: JumpTarget;
};

export const JUMP_TARGET_KINDS = ['next', 'end', 'submitInvalid', 'question'] as const;
export type JumpTargetKind = (typeof JUMP_TARGET_KINDS)[number];

/** 富文本格式：所有字段可选；颜色为 CSS 颜色字符串；字号为带单位的字符串 '16px'/'1.2em' 等 */
export type RichTextFormat = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** 文字颜色，如 '#ff0000' */
  color?: string;
  /** 字体名，如 '"Microsoft YaHei", sans-serif' */
  fontFamily?: string;
  /** 字号，如 '16px' */
  fontSize?: string;
  /** 文本对齐：'left' | 'center' | 'right' */
  align?: 'left' | 'center' | 'right';
};

/** 富文本题干：text 用于必填校验与纯文本场景（如导出/搜索），html 用于渲染 */
export type RichText = {
  text: string;
  /** 内联 HTML（含 <b>/<i>/<u>/<span style="..."> 等）；编辑时存的是渲染结果 */
  html?: string;
  /** 整体格式（适用于无内联格式的纯整体样式）；与 html 互为兜底 */
  format?: RichTextFormat;
};

/** 兼容老数据：title 可为 string 或 RichText 形态 */
export type TitleLike = string | RichText;

export function isRichText(value: unknown): value is RichText {
  return !!value && typeof value === 'object' && !Array.isArray(value) && typeof (value as { text?: unknown }).text === 'string';
}

/** 把 title 统一抽出纯文本（用于校验/必填判断/导出） */
export function titleText(value: TitleLike | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return (value as RichText).text ?? '';
}

/** 把 title 转成可渲染 HTML（字符串原样返回，富文本对象走 format + html 渲染） */
export function titleHTML(value: TitleLike | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return escapeHTML(value);
  const r = value as RichText;
  if (r.html && r.html.trim() !== '') return r.html; // 编辑器写入的 HTML
  const f = r.format ?? {};
  const text = escapeHTML(r.text ?? '');
  const openStyle: string[] = [];
  if (f.fontFamily) { openStyle.push(`font-family:${f.fontFamily}`); }
  if (f.fontSize) { openStyle.push(`font-size:${f.fontSize}`); }
  if (f.color) { openStyle.push(`color:${f.color}`); }
  const tagOpen = openStyle.length ? `<span style="${openStyle.join(';')}">` : '';
  const tagClose = openStyle.length ? `</span>` : '';
  let html = `${tagOpen}${text}${tagClose}`;
  if (f.bold) html = `<strong>${html}</strong>`;
  if (f.italic) html = `<em>${html}</em>`;
  if (f.underline) html = `<u>${html}</u>`;
  if (f.align) html = `<div style="text-align:${f.align}">${html}</div>`;
  return html;
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** 把 title 转换为 RichText（若已是 string 则包装成对象；已是 RichText 则返回原对象） */
export function ensureRichText(value: TitleLike | undefined | null): RichText {
  if (isRichText(value)) return value;
  return { text: typeof value === 'string' ? value : '', format: {} };
}

export type Option = {
  value: string;
  quota?: number;
  onQuotaFull?: 'jumpNext' | 'end';
  /** 评分题：该选项分值 */
  score?: number;
  /** 0902 选项附属填空：开启后填写者选中该选项时可补充一段文本（存于 answers._optionText[题号][选项值]） */
  allowText?: boolean;
  /** 0904 多选互斥：勾选该选项时自动取消所有普通选项；勾选普通选项时自动取消所有互斥选项（支持多组） */
  exclusive?: boolean;
  [k: string]: any;
};

export type SliderParams = {
  min: number;
  max: number;
  /** 数值间隔（>0）；allowDecimal=false 时必须是 ≥1 的整数（0.5 倍数需 allowDecimal=true） */
  step?: number;
  unit?: string;
  /** 最小值端文本（≤24 字） */
  minLabel?: string;
  /** 最大值端文本（≤24 字） */
  maxLabel?: string;
  /** 不允许小数：true 时 step 必须是 ≥1 的整数；默认 false */
  allowDecimal?: boolean;
};

/** 各题型 params 的并集（按 type 取用；滑动条另见 SliderParams） */
export type QuestionParams = {
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  minLabel?: string;
  maxLabel?: string;
  allowDecimal?: boolean;
  /** 开放题字数上限 */
  maxLen?: number;
  /** 文本题答案配图 */
  allowImageUpload?: boolean;
  /** 矩阵量表的行 / 矩阵填空（0902）的行标题 */
  rows?: string[];
  columns?: string[];
  columnScores?: (number | null)[];
  /** 1627 矩阵列填空：与 columns 等长的 boolean 数组，开启后填写者选中该列时，单元格下方出现文本框（答案存 _matrixColText） */
  columnAllowText?: boolean[];
  /** 0902 多选题选数限制：最少 / 最多可选几项（缺省或 0 = 不限）；提示文案可配 */
  minSelect?: number;
  maxSelect?: number;
  minSelectHint?: string;
  maxSelectHint?: string;
  /** 0903 填空题格式校验：姓名 / 手机号 / 身份证号（缺省不校验） */
  validation?: TextValidation;
  /** 0904 矩阵填空行级校验：key=行标题，value=该行字符串校验规则（多列时作用于该行所有文本列） */
  rowValidations?: Record<string, TextValidation>;
  /** 0907 矩阵填空按行独立控件：key=行标题，value 该行的控件类型（text=文本输入框/select=下拉选择框）；
   *  不配置/缺省视为 text；下拉行需配 options。答题时只影响该行单元格的渲染，不改 rowValidations 校验。 */
  rowControls?: Record<string, MatrixRowControl>;
  /** 0907 矩阵填空的列：缺省 = 老数据的单列「填写者作答」（答案 { 行: 文本 }）；配置后答案变为 { 行: { 列: 文本 } } */
  textColumns?: MatrixTextColumn[];
  /** 0907 矩阵滑动题：与 rows 对齐的行配图（data URL），null/缺省 = 该行无图 */
  rowImages?: (string | null)[];
  [k: string]: any;
};

/** 0907 矩阵填空的一列：kind=text 文本输入框（默认）/ select 下拉选择框（options 为下拉项，填写者不能自由输入）；
 *  validation 为该列的格式校验（可选，优先于行级 rowValidations；仅文本列有效） */
export type MatrixTextColumn = { title: string; kind?: 'text' | 'select'; options?: string[]; validation?: TextValidation };

/** 0907 矩阵填空按行独立控件：每行可单独选 text/select，下拉行带 options；不再走列级设置 */
export type MatrixRowControl = { kind: 'text' | 'select'; options?: string[] };

export type Question = {
  id: string;
  type: QuestionType;
  /** 主标题：可为 string（兼容老数据）或 RichText 对象 */
  title: TitleLike;
  description?: string;
  /** 描述配图（前端同学版本的题图字段；渲染时与 image 互为兜底） */
  descriptionImage?: string;
  /** 当 title 是 RichText 时是否必填（默认 true；标题说明/分页栏默认 false） */
  required: boolean;
  requiredRead?: boolean;
  /** 题干配图（data URL）——老字段，单图 */
  image?: string;
  /** 0903 题干多图（data URL 或 http(s)）；与老字段 image / descriptionImage 合并显示，见 questionImages() */
  images?: string[];
  /** 在前置页显示（随协议页一起填写，如基本信息） */
  onPreface?: boolean;
  options?: Option[];
  params?: QuestionParams;
  displayLogic: DisplayLogic;
  /** 1627 题目关联：仅在前置题选中指定选项时显示当前题；与 displayLogic 是 AND 关系（关联命中 + displayLogic 命中 才显示） */
  correlation?: Correlation[];
  /** 0904 跳题逻辑：答完此题后跳到指定目标；与 displayLogic 互不影响（displayLogic 控制本题是否展示，jumpLogic 控制答完此题后下一题是谁） */
  jumpLogic?: JumpLogic;
  /** 0907 选项关联：本题（目标题 B）的选项随源题 A 的选项联动展示；无规则命中时本题不显示 */
  optionLinks?: OptionLink[];
  [k: string]: any;
};

/** 前置页独立填写项（仅 text / longtext，随协议页一起填写） */
export type PrefaceQuestion = {
  id: string;
  type: 'text' | 'longtext';
  title: TitleLike;
  required: boolean;
  params?: { maxLen?: number; allowImageUpload?: boolean };
  [k: string]: any;
};

/** 文本题答案配图：答案为 { text, images } 对象 */
export type TextAnswer = { text: string; images: string[] };

export const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;
export const MAX_ANSWER_IMAGES = 3;

export function textAnswerText(value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value) && typeof (value as TextAnswer).text === 'string') {
    return (value as TextAnswer).text;
  }
  return typeof value === 'string' ? value : '';
}

export function textAnswerImages(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const images = (value as Partial<TextAnswer>).images;
  return Array.isArray(images) ? images.filter((image): image is string => typeof image === 'string') : [];
}

export function withTextAnswer(text: string, images: string[]): TextAnswer {
  return { text, images };
}

/** 地理位置限制（现场签到围栏） */
export type GeoFenceSettings = {
  enabled?: boolean;
  /** 锚点纬度（管理员到场点「使用我当前位置」写入） */
  lat?: number;
  /** 锚点经度 */
  lng?: number;
  /** 允许半径（米），默认 100 */
  radius?: number;
  /** 定位精度上限（米）；超过判不可信，要求重试，默认 100 */
  maxAccuracy?: number;
  /** 取不到定位时的处理：block（拒绝）/ warn（警告但允许）/ allow（跳过），默认 block */
  onError?: 'block' | 'warn' | 'allow';
};

export type SurveySettings = {
  stopCollect?: boolean;
  anonymous?: boolean;
  voiceInput?: boolean;
  whoCanFill?: string;
  loginRequired?: boolean;
  inviteToken?: string;
  /** 每人限填一次（按浏览器设备识别） */
  oncePerUser?: boolean;
  /** 填写草稿恢复：开启后填写者退出/刷新可恢复上次未提交的答案（默认 true；含敏感字段问卷可关闭） */
  draftRecovery?: boolean;
  preface?: { title?: string; body?: string; image?: string; requireCheck?: boolean; questions?: PrefaceQuestion[] };
  signature?: { enabled?: boolean; mode?: 'draw' | 'upload'; required?: boolean; onPreface?: boolean };
  /** 地理位置限制（现场签到围栏） */
  geoFence?: GeoFenceSettings;
  feishuTable?: { appToken?: string; tableId?: string; url?: string; fieldNames?: string[] };
  editAnswer?: { enabled?: boolean; tokenTtlHours?: number };
  timeLimit?: { enabled?: boolean; start?: string; end?: string };
  quotaEnabled?: boolean;
  quota?: number;
  submitLimitEnabled?: boolean;
  submitLimit?: number;
  resultPage?: { title?: string; body?: string };
  redirectUrl?: { enabled?: boolean; url?: string };
  feishu?: { enabled?: boolean; webhookUrl?: string; bearerToken?: string; publicBaseUrl?: string };
  [k: string]: any;
};

export type SurveyConfig = {
  id: string;
  header: { mainTitle: string; subTitle?: string };
  questions: Question[];
  settings: SurveySettings;
  [k: string]: any;
};
/* ---------------- 0902 选项附属填空 ---------------- */

/** answers 里保存选项附属填空文本的保留键：answers._optionText[题号][选项值] = 文本（后端 answer-rules.mjs 同名） */
export const OPTION_TEXT_KEY = '_optionText';

/** 1627 answers 里保存矩阵列附属填空文本的保留键：answers._matrixColText[题号][行标题] = 文本 */
export const MATRIX_COL_TEXT_KEY = '_matrixColText';

/** 取某题的 { 选项值: 填写文本 }；没有则 undefined */
export function optionTextsOf(answers: Record<string, any> | undefined | null, qid: string): Record<string, string> | undefined {
  const all = answers?.[OPTION_TEXT_KEY];
  if (!all || typeof all !== 'object' || Array.isArray(all)) return undefined;
  const m = all[qid];
  return m && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, string>) : undefined;
}

/** 写入某题某选项的附属文本，返回新的 answers（不可变更新） */
export function withOptionText(answers: Record<string, any>, qid: string, optValue: string, text: string): Record<string, any> {
  const cur = answers?.[OPTION_TEXT_KEY];
  const all = (cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {}) as Record<string, Record<string, string>>;
  return { ...answers, [OPTION_TEXT_KEY]: { ...all, [qid]: { ...(all[qid] ?? {}), [optValue]: text } } };
}

/** 1627 取某矩阵题的 { 行标题: 文本 } 映射；没有则 undefined */
export function matrixColTextsOf(answers: Record<string, any> | undefined | null, qid: string): Record<string, string> | undefined {
  const all = answers?.[MATRIX_COL_TEXT_KEY];
  if (!all || typeof all !== 'object' || Array.isArray(all)) return undefined;
  const m = all[qid];
  return m && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, string>) : undefined;
}

/** 1627 写入某矩阵题某行的列附属文本，返回新的 answers */
export function withMatrixColText(answers: Record<string, any>, qid: string, row: string, text: string): Record<string, any> {
  const cur = answers?.[MATRIX_COL_TEXT_KEY];
  const all = (cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {}) as Record<string, Record<string, string>>;
  return { ...answers, [MATRIX_COL_TEXT_KEY]: { ...all, [qid]: { ...(all[qid] ?? {}), [row]: text } } };
}

/** 选项值 + 附属填空 → "选项：文本"（该选项没填文本时原样返回；非字符串答案原样返回） */
export function decorateChoiceAnswer(value: unknown, textMap?: Record<string, string> | null): unknown {
  if (!textMap) return value;
  const dec = (v: unknown) => {
    const t = typeof v === 'string' ? textMap[v] : undefined;
    return typeof t === 'string' && t.trim() !== '' ? `${v}：${t.trim()}` : v;
  };
  if (Array.isArray(value)) return value.map(dec);
  return dec(value);
}

/* ---------------- 0902 多选题选数限制 ---------------- */

export type SelectLimit = { min: number | null; max: number | null; minHint: string; maxHint: string };

/** 多选题的选数限制；未配置（或不是多选题）返回 null。缺省提示文案："至少选择N项" / "最多只能选择N项" */
export function selectLimitOf(q: { type?: string; params?: QuestionParams | any } | undefined | null): SelectLimit | null {
  if (!q || !(SELECT_LIMIT_TYPES as readonly string[]).includes(q.type ?? '')) return null;
  const p = (q.params ?? {}) as QuestionParams;
  const min = Number.isInteger(p.minSelect) && (p.minSelect as number) > 0 ? (p.minSelect as number) : null;
  const max = Number.isInteger(p.maxSelect) && (p.maxSelect as number) > 0 ? (p.maxSelect as number) : null;
  if (min === null && max === null) return null;
  const hint = (custom: unknown, fallback: string) => (typeof custom === 'string' && custom.trim() !== '' ? custom.trim() : fallback);
  return {
    min,
    max,
    minHint: min === null ? '' : hint(p.minSelectHint, `至少选择${min}项`),
    maxHint: max === null ? '' : hint(p.maxSelectHint, `最多只能选择${max}项`),
  };
}

/** 校验勾选数量：不合规返回提示文案，合规返回 null。非必填且一项未选时不校验（与后端 answer-rules.mjs 同规则） */
export function selectLimitError(q: { type?: string; required?: boolean; params?: any }, value: unknown): string | null {
  const limit = selectLimitOf(q);
  if (!limit) return null;
  const count = Array.isArray(value) ? value.length : 0;
  if (count === 0 && !q.required) return null;
  if (limit.min !== null && count < limit.min) return limit.minHint;
  if (limit.max !== null && count > limit.max) return limit.maxHint;
  return null;
}

/* ---------------- 0903 填空题格式校验 ---------------- */

export type TextValidation = 'name' | 'phone' | 'idcard' | 'date';

/** 0907 日期校验：YYYY-MM-DD 且必须是真实存在的日期（与后端 answer-rules.mjs isDateString 同规则） */
export function isDateString(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1900 || y > 2200 || mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/** 本地日期 → YYYY-MM-DD */
export function formatDateYMD(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/** 与后端 answer-rules.mjs TEXT_VALIDATIONS 同一套规则：前端先拦，服务端兜底 */
/** 0907 身份证号校验码（GB 11643-1999）：前 17 位加权求和 mod 11 → 校验码，末位必须匹配 */
const ID_WEIGHTS: number[] = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
const ID_CHECK_CODES = '10X98765432'; // 余数 0-10 → 校验码
function idcardValid(s: string): boolean {
  const m = /^(\d{17})([\dXx])$/.exec(s);
  if (!m) return false;
  let sum = 0;
  for (let i = 0; i < 17; i += 1) sum += Number(m[1][i]) * ID_WEIGHTS[i];
  return m[2].toUpperCase() === ID_CHECK_CODES[sum % 11];
}

export const TEXT_VALIDATIONS: Record<TextValidation, { label: string; hint: string; maxLength: number; inputMode?: 'numeric' | 'text'; test: (s: string) => boolean }> = {
  name: { label: '姓名校验', hint: '姓名须为 2-15 位纯汉字', maxLength: 15, test: (s) => /^\p{Script=Han}{2,15}$/u.test(s) },
  phone: { label: '手机号校验', hint: '请输入正确的 11 位手机号', maxLength: 11, inputMode: 'numeric', test: (s) => /^1[3-9]\d{9}$/.test(s) },
  idcard: { label: '身份证号校验', hint: '请输入正确的 18 位身份证号', maxLength: 18, test: idcardValid },
  // 0907 日期：填写端渲染日历选择器（DatePickerInput），值固定 YYYY-MM-DD
  date: { label: '日期校验', hint: '请选择日期', maxLength: 10, test: isDateString },
};

/** 0903 / 0904 文本类校验：text + longtext 题级，matrixtext 行级 */

/** 题级校验：text / longtext；其它题型（含 matrixtext 题级）返回 null */
export function textValidationOf(q: { type?: string; params?: any } | undefined | null): TextValidation | null {
  const v = q?.params?.validation;
  return (q?.type === 'text' || q?.type === 'longtext') && typeof v === 'string' && v in TEXT_VALIDATIONS ? (v as TextValidation) : null;
}

/** 矩阵填空行级校验：返回该行配置的校验种类；无配置返回 null */
export function matrixTextRowValidationOf(
  q: { type?: string; params?: any } | undefined | null,
  row: string,
): TextValidation | null {
  if (q?.type !== 'matrixtext') return null;
  const map = q?.params?.rowValidations;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  const v = map[row];
  return typeof v === 'string' && v in TEXT_VALIDATIONS ? (v as TextValidation) : null;
}

/**
 * 不合规返回提示文案；合规、未配置或空值（交给必填校验）返回 null。
 * 题型：
 *  - text / longtext：取整道题文本作校验
 *  - matrixtext：逐行校验，返回首个不合规行的提示（带行标题前缀「<行>：」）
 */
export function textFormatError(q: { type?: string; params?: any; id?: string }, value: unknown): string | null {
  if (q.type === 'text' || q.type === 'longtext') {
    const kind = textValidationOf(q);
    if (!kind) return null;
    const s = textAnswerText(value).trim();
    if (s === '') return null;
    return TEXT_VALIDATIONS[kind].test(s) ? null : TEXT_VALIDATIONS[kind].hint;
  }
  if (q.type === 'matrixtext') {
    const rows: string[] = Array.isArray(q.params?.rows) ? q.params.rows : [];
    if (rows.length === 0) return null;
    const map: Record<string, unknown> = value && typeof value === 'object' && !Array.isArray(value) ? (value as any) : {};
    // 0907 多列：行级校验作用于该行的每个文本列（下拉列的值来自固定选项，不校验）
    const cols = matrixTextColumnsOf(q);
    for (const r of rows) {
      const kind = matrixTextRowValidationOf(q, r);
      if (cols.length === 0) {
        if (!kind) continue;
        const s = String(map[r] ?? '').trim();
        if (s === '') continue; // 空值交给必填校验
        if (!TEXT_VALIDATIONS[kind].test(s)) return `${r}：${TEXT_VALIDATIONS[kind].hint}`;
        continue;
      }
      for (let i = 0; i < cols.length; i += 1) {
        const ck = matrixTextCellValidationOf(q, r, cols[i]);
        if (!ck) continue;
        const s = matrixTextCellValue(map[r], cols[i].title, i).trim();
        if (s === '') continue;
        if (!TEXT_VALIDATIONS[ck].test(s)) return `${r}·${cols[i].title}：${TEXT_VALIDATIONS[ck].hint}`;
      }
    }
    return null;
  }
  return null;
}

/** 多列矩阵填空某个单元格的有效校验：列级 validation 优先，其次行级 rowValidations；下拉列不校验 */
export function matrixTextCellValidationOf(q: { type?: string; params?: any } | undefined | null, row: string, col: MatrixTextColumn): TextValidation | null {
  if ((col.kind ?? 'text') !== 'text') return null;
  if (typeof col.validation === 'string' && col.validation in TEXT_VALIDATIONS) return col.validation;
  return matrixTextRowValidationOf(q, row);
}

/* ---------------- 0907 矩阵填空多列 ---------------- */

/** 矩阵填空的列配置（规整后）；老数据 / 未配置返回 []（= 单列「填写者作答」，答案 { 行: 文本 }） */
export function matrixTextColumnsOf(q: { type?: string; params?: any } | undefined | null): MatrixTextColumn[] {
  if (q?.type !== 'matrixtext') return [];
  const raw = q?.params?.textColumns;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is MatrixTextColumn => !!c && typeof c === 'object' && typeof (c as MatrixTextColumn).title === 'string')
    .map((c) => ({
      title: c.title,
      kind: c.kind === 'select' ? 'select' : 'text',
      options: Array.isArray(c.options) ? c.options.filter((o): o is string => typeof o === 'string') : [],
      validation: typeof c.validation === 'string' && c.validation in TEXT_VALIDATIONS ? c.validation : undefined,
    }));
}

/**
 * 取矩阵填空某行某列的单元格文本：
 *  - 多列答案 { 行: { 列: 文本 } } → 按列标题取
 *  - 老单列答案 { 行: 文本 }（配置改成多列后的历史提交）→ 只算作第 1 列
 */
export function matrixTextCellValue(rowValue: unknown, colTitle: string, colIndex: number): string {
  if (rowValue === undefined || rowValue === null) return '';
  if (typeof rowValue === 'object' && !Array.isArray(rowValue)) {
    const v = (rowValue as Record<string, unknown>)[colTitle];
    return v === undefined || v === null ? '' : String(v);
  }
  return colIndex === 0 ? String(rowValue) : '';
}

/** 矩阵填空某行的展示文本："列A 值；列B 值"（多列）或原文本（单列） */
export function matrixTextRowText(rowValue: unknown): string {
  if (rowValue === undefined || rowValue === null) return '';
  if (typeof rowValue === 'object' && !Array.isArray(rowValue)) {
    return Object.entries(rowValue as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
      .map(([c, v]) => `${c} ${String(v)}`)
      .join('；');
  }
  return String(rowValue);
}

/* ---------------- 0907 矩阵滑动题 ---------------- */

/** 矩阵滑动题各行配图（与 rows 对齐；无图为 null） */
export function matrixSliderImagesOf(q: { params?: any } | undefined | null): (string | null)[] {
  const rows: string[] = Array.isArray(q?.params?.rows) ? q!.params.rows : [];
  const imgs = Array.isArray(q?.params?.rowImages) ? q!.params.rowImages : [];
  return rows.map((_, i) => (typeof imgs[i] === 'string' && imgs[i] !== '' ? (imgs[i] as string) : null));
}

/**
 * 矩阵类题型「是否已完整作答」（必填校验用；Runner / PreviewPanel / 服务端 answerEmpty 同规则）：
 *  - matrix：每行都选了列
 *  - matrixtext：每行（多列时每个单元格）都非空白
 *  - matrixslider：每行都有数值
 * 非矩阵类题型返回 null（交给调用方原有规则）。
 */
export function matrixLikeAnswered(q: { type?: string; params?: any }, value: unknown): boolean | null {
  if (q.type !== 'matrix' && q.type !== 'matrixtext' && q.type !== 'matrixslider') return null;
  const rows: string[] = Array.isArray(q.params?.rows) ? q.params.rows : [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const map = value as Record<string, unknown>;
  if (q.type === 'matrixslider') return rows.every((r) => typeof map[r] === 'number' && Number.isFinite(map[r] as number));
  if (q.type === 'matrixtext') {
    const cols = matrixTextColumnsOf(q);
    if (cols.length === 0) return rows.every((r) => map[r] !== undefined && map[r] !== null && String(map[r]).trim() !== '');
    return rows.every((r) => cols.every((c, i) => matrixTextCellValue(map[r], c.title, i).trim() !== ''));
  }
  return rows.every((r) => map[r] !== undefined && map[r] !== null && String(map[r]).trim() !== '');
}

/* ---------------- 0907 选项关联：填写端过滤 ---------------- */

function isOptionLink(x: unknown): x is OptionLink {
  return !!x && typeof x === 'object' && typeof (x as OptionLink).questionId === 'string' && typeof (x as OptionLink).optionValue === 'string' && Array.isArray((x as OptionLink).options);
}

/** 源题答案是否选中了指定选项（单选/下拉：相等；多选/排序：包含） */
function answerHasOption(ans: unknown, optionValue: string): boolean {
  if (ans === undefined || ans === null) return false;
  if (Array.isArray(ans)) return ans.some((v) => String(v) === optionValue);
  return String(ans) === optionValue;
}

/**
 * 本题在当前作答下允许展示的选项集合：
 *  - 未配置选项关联 → null（不限制，展示全部）
 *  - 有配置 → 所有命中规则（源题可见且选中了该选项）的 options 并集；没有命中 → 空集合（本题整体不显示）
 */
export function allowedOptionsOf(q: { optionLinks?: unknown } | undefined | null, answers: Record<string, unknown>, visibleIds?: Set<string>): Set<string> | null {
  const links = Array.isArray(q?.optionLinks) ? (q!.optionLinks as unknown[]).filter(isOptionLink) : [];
  if (links.length === 0) return null;
  const allowed = new Set<string>();
  for (const l of links) {
    if (visibleIds && !visibleIds.has(l.questionId)) continue;
    if (!answerHasOption(answers?.[l.questionId], l.optionValue)) continue;
    for (const o of l.options) if (typeof o === 'string') allowed.add(o);
  }
  return allowed;
}

/** 选项关联：没有任何规则命中 → 本题不显示（display-logic-bridge / 后端 display-logic.mjs 同规则） */
export function evaluateOptionLinks(q: { optionLinks?: unknown } | undefined | null, answers: Record<string, unknown>, visibleIds?: Set<string>): boolean {
  const allowed = allowedOptionsOf(q, answers, visibleIds);
  return allowed === null || allowed.size > 0;
}

/**
 * 把所有配置了选项关联的题的答案裁剪到「当前允许展示」的选项内（源题切换后，目标题里已不可见的勾选立即清掉）。
 * 返回新对象；没有需要裁剪的内容时返回原对象。
 */
export function pruneOptionLinkAnswers(config: { questions?: Question[] } | null | undefined, answers: Record<string, any>, visibleIds: Set<string>): Record<string, any> {
  let out = answers;
  for (const q of config?.questions ?? []) {
    if (!Array.isArray(q.optionLinks) || q.optionLinks.length === 0) continue;
    const cur = out[q.id];
    if (cur === undefined || cur === null) continue;
    const allowed = allowedOptionsOf(q, out, visibleIds);
    if (allowed === null) continue;
    if (Array.isArray(cur)) {
      const kept = cur.filter((v) => typeof v === 'string' && allowed.has(v));
      if (kept.length === cur.length) continue;
      out = { ...out };
      if (kept.length) out[q.id] = kept; else delete out[q.id];
    } else if (typeof cur === 'string') {
      if (allowed.has(cur)) continue;
      out = { ...out };
      delete out[q.id];
    }
  }
  return out;
}

/* ---------------- 0903 题干多图 ---------------- */

/** 每题最多几张题图（data URL 直接进问卷配置，太多会撑爆请求体；与后端 MAX_QUESTION_IMAGES 一致） */
export const MAX_QUESTION_IMAGES = 9;

/** 题目的全部题图：新字段 images + 老字段 image / descriptionImage（老图在前），与后端 questionImagesOf 同规则 */
export function questionImages(q: { images?: unknown; image?: unknown; descriptionImage?: unknown } | undefined | null): string[] {
  const list = Array.isArray(q?.images) ? (q!.images as unknown[]).filter((x): x is string => typeof x === 'string' && x !== '') : [];
  const legacy = typeof q?.image === 'string' && q.image !== '' ? q.image : typeof q?.descriptionImage === 'string' && q.descriptionImage !== '' ? q.descriptionImage : null;
  if (legacy && !list.includes(legacy)) return [legacy, ...list];
  return list;
}

/* ---------------- 0903 排序题 ---------------- */

/** 排序结果 → "A→B→C"（按名次、→ 分隔，与后端导出 / 推送 / PDF 区分开） */
export function rankingText(value: unknown): string {
  return Array.isArray(value) ? value.map((x) => String(x)).join('→') : '';
}

/** 排序结果 → 选项序号 "2,1,3"（1 起；不在选项表里的历史值原样保留） */
export function rankingIndexText(optionValues: string[], value: unknown): string {
  if (!Array.isArray(value)) return '';
  return value.map((v) => { const i = optionValues.indexOf(String(v)); return i >= 0 ? String(i + 1) : String(v); }).join(',');
}
